import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Modal, Button, Input, Spin } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useDispatch, useSelector } from "react-redux";
import { useDeletePermissionMutation } from "../../../redux/permissions/permissionsAPI";
import { updateOpenModalDeletePermission } from "../../../redux/permissions/permissionsSlice";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
// UTILS
import { styleDeleteButton } from "../../../utils/utils";
// COMPONENTS
import GlobalAlert2 from "../../home/GlobalAlert2";
// INTERFACES
import { RootState } from "../../../app/store";

function PermissionsDeleteModal() {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */
	const dispatch = useDispatch();
	const [t] = useTranslation("global");

	// ************************************************ */
	// USE STATE VARIABLES **************************** */
	const [confirmName, setConfirmName] = useState("");

	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */
	const { permission, openModalDeletePermission } = useSelector((state: RootState) => state.permission);
	const { theme } = useSelector((state: RootState) => state.home);

	// ************************************************ */
	// SERVICES AND API CALLS ************************* */
	const [deletePermission, { isLoading, isSuccess, error, isError, reset: resetDeletePermission }] =
		useDeletePermissionMutation();

	// ************************************************ */
	// FUNCTIONS ************************************** */
	const disableButton = () => {
		if (confirmName && permission && permission.name && confirmName.toLowerCase() === permission.name.toLowerCase()) {
			return false;
		}
		return true;
	};

	const onClickClose = () => {
		dispatch(updateOpenModalDeletePermission(false));
		setConfirmName("");
		resetDeletePermission();
	};

	const handleDeleteItem = async () => {
		const token = await GETJwtToken();
		deletePermission({ id: permission.id, token });
	};

	const handleSubmitDeleteItem = async (event: any) => {
		if (event.key === "Enter" && !disableButton()) {
			const token = await GETJwtToken();
			deletePermission({ id: permission.id, token });
		}
	};

	// ************************************************* */
	// USE EFFECT ************************************** */
	useEffect(() => {
		if (isSuccess || isError) {
			onClickClose();
		}
	}, [isSuccess, isError]);

	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<Modal onOk={onClickClose} closable={false} onCancel={onClickClose} open={openModalDeletePermission} footer={null}>
			<Spin spinning={isLoading}>
				<div>
					<div className='text-center'>
						<FontAwesomeIcon
							icon={faTriangleExclamation}
							className='generalStyles__warning generalStyles__deleteItemIcon'
						/>
						<p className='mt-3 drawer__text'>
							{t("organizationManagement.deleteDescription.text1")}
							<b className='generalStyles__warning'>{t("organizationManagement.deleteDescription.text2")} </b>
							{t("general.thePermission")}{" "}
							<b className='generalStyles__warning'>{permission && permission.name ? permission.name : ""} </b>
							{t("general.confirmName")}{" "}
						</p>
						<Input
							placeholder={t("general.name")}
							onChange={(e) => {
								setConfirmName(e.target.value);
							}}
							value={confirmName}
							onKeyDown={handleSubmitDeleteItem}
							status='warning'
						/>
					</div>
					<div className='generalStyles__flexEnd mt-4'>
						<div>
							<Button
								htmlType='submit'
								className={styleDeleteButton(disableButton(), theme)}
								onClick={handleDeleteItem}
								disabled={disableButton()}
							>
								{t("organizationManagement.delete")}
							</Button>
							<Button onClick={onClickClose} className='buttonStyle__4'>
								{t("organizationManagement.close")}
							</Button>
						</div>
					</div>
				</div>
			</Spin>
			{/** ---------------------------------------------------- */}
			{/** GLOBAL ALERT */}
			<GlobalAlert2 isError={isError} isSuccess={isSuccess} requestType='DELETE' error={error} name='Permission' />
		</Modal>
	);
}

export default PermissionsDeleteModal;
